import React, { useState } from 'react';
import { supabase } from '../supabaseClient'; 
import { useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';
import AirportInput from './AirportInput'; // <--- IMPORT DU NOUVEAU COMPOSANT

export default function Flights() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    origin: 'YUL',
    destination: 'CDG',
    date: '2026-05-15'
  });
  const [flights, setFlights] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFlights([]);

    try {
      const { data, error } = await supabase.functions.invoke('search-flights', {
        body: formData
      });
      if (error) throw error;
      if (data && data.data) setFlights(data.data);
      else setFlights([]); 
    } catch (err) {
      console.error(err);
      setError("Impossible de trouver des vols. Vérifiez les dates.");
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIQUE DE REDIRECTION RÉELLE (Deep Linking) ---
  const getRealBookingLink = (origin: string, dest: string, date: string) => {
    // On formate la date pour l'URL (YYMMDD)
    const d = new Date(date);
    const year = d.getFullYear().toString().slice(2);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = (d.getDate() + 1).toString().padStart(2, '0');
    const formattedDate = `${year}${month}${day}`;

    // Redirection vers Skyscanner pré-remplie
    return `https://www.skyscanner.ca/transport/flights/${origin.toLowerCase()}/${dest.toLowerCase()}/${formattedDate}`;
  };

  const handleSelect = async (flight: any) => {
    // 1. Vérification connexion
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      const wantToLogin = window.confirm("Connectez-vous pour sauvegarder ce voyage dans votre Espace Membre et continuer vers la réservation.");
      if (wantToLogin) navigate('/login');
      return;
    }

    // 2. Sauvegarder dans la DB
    const { error } = await supabase
      .from('saved_trips')
      .insert([{ user_id: user.id, type: 'flight', details: flight }]);

    if (error) {
      console.error("Erreur save:", error);
    }

    // 3. Ouvrir le site de réservation
    const bookingUrl = getRealBookingLink(formData.origin, formData.destination, formData.date);
    window.open(bookingUrl, '_blank');
    
    // 4. Redirection vers dashboard
    navigate('/club');
  };

  return (
    <div className="w-full max-w-5xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden -mt-10 relative z-20 border border-gray-100 font-sans">
      {/* Header du module */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-5 text-white shadow-md">
        <h2 className="text-xl font-bold flex items-center gap-3">
          ✈️ <span className="tracking-wide">RÉSERVER UN VOL</span>
        </h2>
      </div>
      
      {/* Formulaire */}
      <div className="p-6 bg-white">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 items-end">
          
          {/* CHAMP DÉPART INTELLIGENT */}
          <AirportInput 
            label="Départ"
            icon="🛫"
            placeholder="Ville ou Aéroport (ex: Montréal)"
            value={formData.origin}
            onChange={(code) => setFormData({...formData, origin: code})}
          />

          {/* CHAMP ARRIVÉE INTELLIGENT */}
          <AirportInput 
            label="Arrivée"
            icon="🛬"
            placeholder="Ville ou Aéroport (ex: Paris)"
            value={formData.destination}
            onChange={(code) => setFormData({...formData, destination: code})}
          />

          <div className="flex-1 w-full">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Date</label>
            <input 
              type="date" value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg font-bold text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-gray-50 transition"
            />
          </div>
          
          <button 
            type="submit" disabled={loading}
            className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white font-extrabold py-3 px-8 rounded-lg transition-all shadow-md transform active:scale-95 whitespace-nowrap"
          >
            {loading ? 'Recherche...' : '🔍 VOIR LES OFFRES'}
          </button>
        </form>
      </div>

      {/* Résultats */}
      <div className="bg-gray-50 p-4 space-y-4 min-h-[100px]">
        {flights.length === 0 && !loading && <p className="text-center text-gray-400 py-6 italic">Entrez votre destination pour voir les prix en temps réel.</p>}
        {loading && <div className="text-center py-10 text-blue-600 font-bold animate-pulse">Recherche des meilleurs tarifs en cours...</div>}
        
        {flights.map((flight) => {
          const airline = flight.validatingAirlineCodes[0];
          const price = flight.price.grandTotal;
          const currency = flight.price.currency;
          const itinerary = flight.itineraries[0];
          const duration = itinerary.duration.replace("PT", "").toLowerCase().replace("h", "h ");
          const segments = itinerary.segments;
          const stops = segments.length - 1;
          const logoUrl = `https://pics.avs.io/200/200/${airline}.png`;

          return (
            <div key={flight.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row items-center justify-between group">
              
              {/* Colonne Gauche : Logo */}
              <div className="flex items-center gap-5 mb-4 md:mb-0 w-full md:w-1/3">
                <div className="w-16 h-16 p-2 bg-white border border-gray-100 rounded-lg flex items-center justify-center shadow-sm">
                  <img src={logoUrl} alt={airline} className="max-w-full max-h-full object-contain" onError={(e) => e.currentTarget.src = 'https://cdn-icons-png.flaticon.com/512/723/723955.png'} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{airline === 'WS' ? 'WestJet' : airline === 'TS' ? 'Air Transat' : airline === 'AC' ? 'Air Canada' : airline}</h3>
                  <p className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded inline-block">Vol confirmé</p>
                </div>
              </div>

              {/* Colonne Centre : Détails */}
              <div className="text-center w-full md:w-1/3 mb-4 md:mb-0 border-l border-r border-transparent md:border-gray-100 px-4">
                <div className="flex items-center justify-center gap-3 text-gray-700 font-bold text-lg">
                  <span>{segments[0].departure.iataCode}</span>
                  <div className="flex flex-col items-center w-24">
                    <span className="text-[10px] text-gray-400">Durée: {duration}</span>
                    <div className="w-full h-[2px] bg-gray-300 relative">
                        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${stops === 0 ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                    </div>
                    <span className={`text-[10px] ${stops === 0 ? "text-green-600" : "text-orange-500"} font-medium`}>
                        {stops === 0 ? "Direct" : `${stops} escale(s)`}
                    </span>
                  </div>
                  <span>{segments[segments.length-1].arrival.iataCode}</span>
                </div>
              </div>

              {/* Colonne Droite : Prix */}
              <div className="text-right w-full md:w-1/3 flex flex-col items-end justify-center">
                <div className="mb-2">
                    <span className="text-3xl font-extrabold text-blue-900">{price}</span>
                    <span className="text-sm font-medium text-gray-500 ml-1">{currency}</span>
                </div>
                <button 
                  onClick={() => handleSelect(flight)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-bold text-sm shadow-md transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  RÉSERVER <FaExternalLinkAlt size={12}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
